import { ProspectActionKind, ProspectStatus } from "@prisma/client";
import { createElement, JSX, ReactNode } from "react";

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { ComponentFactory } from "@/app/(components)/_components/ComponentFactory";
import { Components } from "@/app/(components)/_components/Components";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import type { CostConfirmProps } from "@/app/(components)/specifics/billing/CostConfirm/CostConfirm";
import { toInputDate } from "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectActions/ProspectActions.logic";
import { addClientProspect } from "@/app/actions/prospect-search/prospects/action";
import { hideCrispChat, showCrispChat } from "@/app/libs/CrispChat";
import { TKey } from "@/app/translations/TKey";
import { CallFile } from "@/app/types/call/CallFile";
import { CallScopeFilter } from "@/app/types/call/CallScopeFilter";
import { PageParams } from "@/app/types/common/DefaultProps";
import { CompanyMemberItem } from "@/app/types/company/CompanyMemberItem";
import { ImportedProfileAnalysisPools } from "@/app/types/imported-profile/ImportedProfileAnalysisContext";
import { ImportedProfileListTab } from "@/app/types/imported-profile/ImportedProfileListTab";
import { ProcessingJobData } from "@/app/types/job/ProcessingJob";
import { InsightCallSource } from "@/app/types/project/InsightCallSource";
import { ProjectMemberData } from "@/app/types/project/ProjectMember";
import { ProspectActionItem, ProspectActionTypeItem } from "@/app/types/prospect/ProspectAction";
import { ProspectPrefill } from "@/app/types/prospect/ProspectPrefill";
import { ProspectInput } from "@/app/types/prospect/ProspectRecord";
import { ProspectSearchCriteria } from "@/app/types/prospect/ProspectSearchCriteria";

export type ModalOverlay = {
  title: ReactNode;
  type: ModalType;
  component: JSX.Element | null;
  footer: ReactNode;
};

export type ModalState = {
  isOpen: boolean;
  title: ReactNode;
  component: JSX.Element | null;
  footer: ReactNode | null;
  type: ModalType | null;
  closeWhenClickOutside: boolean;
  onLoginOpenModal: {
    component: Components;
    title: ReactNode;
    props?: {};
    type?: ModalType;
  } | null;
  openModal(
    component: Components,
    title: ReactNode,
    props?: {},
    type?: ModalType,
    footer?: ReactNode,
    closeWhenClickOutside?: boolean,
  ): void;
  openSignIn(params: PageParams, onLoginOpenModal?: ModalState["onLoginOpenModal"]): void;
  openSignUp(
    params: PageParams,
    onLoginOpenModal?: ModalState["onLoginOpenModal"],
    email?: string,
    inviteToken?: string,
  ): void;
  openDemo(): void;
  openBuyCredits(params: PageParams): void;
  openJobFailure(params: PageParams, jobs: ProcessingJobData[]): void;
  openConfirmDialog(
    params: PageParams,
    title: TKey,
    text: TKey,
    confirmLabel: TKey,
    onConfirm: () => void,
    overlay?: boolean,
  ): void;
  openImportedProfileAnalysisChoice(
    params: PageParams,
    avatarId: number,
    pools: ImportedProfileAnalysisPools,
    needUpdate: boolean,
    onLaunched?: () => void,
  ): void;
  openImportedProfileAnalysis(
    params: PageParams,
    avatarId: number,
    poolTotal: number,
    poolDone: number,
    needUpdate: boolean,
    onLaunched?: () => void,
  ): void;
  openImportedProfileExtensionGuide(params: PageParams): void;
  openImportedProfilesList(
    params: PageParams,
    avatarId: number,
    criteria: ProspectSearchCriteria | null,
    tab?: ImportedProfileListTab,
    onProspectAdded?: () => void,
  ): void;
  openProspectsFound(params: PageParams, avatarId: number): void;
  openProspectForm(
    params: PageParams,
    avatarId: number,
    criteria: ProspectSearchCriteria | null,
    onAdded?: () => void,
  ): void;
  openInsightCalls(
    params: PageParams,
    insightId: number,
    source: InsightCallSource,
    title: string,
    filter: CallScopeFilter,
  ): void;
  openCallDetailOverlay(params: PageParams, callFile: CallFile): void;
  openExportCall(params: PageParams, callId: number): void;
  openAdminUserProfile(params: PageParams, uuid: string, fullName?: string): void;
  openProspectDetail(
    params: PageParams,
    prospectId: number,
    fullName: string,
    client?: boolean,
    withContext?: boolean,
    withHistory?: boolean,
  ): void;
  openProspectActionConfirm(
    params: PageParams,
    prospectId: number,
    kind: ProspectActionKind,
    label: string,
    typeId?: number,
    onDone?: () => void,
  ): void;
  openProspectActionType(
    params: PageParams,
    projectId: number,
    onCreated?: (type: ProspectActionTypeItem) => void,
  ): void;
  openProspectActionEdit(
    params: PageParams,
    action: ProspectActionItem,
    label: string,
    onDone?: () => void,
  ): void;
  openProspectStatusReason(
    params: PageParams,
    prospectId: number,
    status: ProspectStatus,
    onDone?: () => void,
  ): void;
  openProjectMemberForm(
    params: PageParams,
    projectId: number,
    options?: {
      member?: ProjectMemberData;
      onSaved?: (member: ProjectMemberData) => void;
    },
  ): void;
  openDeleteProjectMember(
    params: PageParams,
    projectId: number,
    member: ProjectMemberData,
    onDeleted?: (member: ProjectMemberData) => void,
  ): void;
  openAddCallType(params: PageParams, projectId: number, onCreated?: (name: string) => void): void;
  openScriptInsightQuotes(
    params: PageParams,
    avatarId: number,
    insightId: number,
    label: string,
  ): void;
  openEditCallType(
    params: PageParams,
    projectId: number,
    name: string,
    onSaved?: (name: string) => void,
  ): void;
  openDeleteCallType(
    params: PageParams,
    projectId: number,
    name: string,
    onDeleted?: (name: string) => void,
  ): void;
  overlays: ModalOverlay[];
  pushOverlay(component: Components, title: ReactNode, props?: {}, type?: ModalType): void;
  openCostConfirmOverlay(
    params: PageParams,
    title: string,
    props: Omit<CostConfirmProps, "params" | "isOverlay">,
  ): void;
  openProspectFormOverlay(
    params: PageParams,
    avatarId: number,
    criteria: ProspectSearchCriteria | null,
    initial: ProspectPrefill,
    onAdded?: (score: number) => void,
  ): void;
  openImportedProfileRejectOverlay(
    params: PageParams,
    matchId: number,
    onRejected?: () => void,
    agent?: boolean,
  ): void;
  openCompanyMemberForm(
    params: PageParams,
    companyId: number,
    onAdded?: () => void,
    member?: CompanyMemberItem,
  ): void;
  closeOverlay(): void;
  setFooter(footer: ReactNode): void;
  setOverlayFooter(footer: ReactNode): void;
  closeModal(): void;
};

export const ModalStore = create<ModalState>()(
  devtools((set, getState) => ({
    isOpen: false,
    title: "",
    component: null,
    footer: null,
    type: null,
    closeWhenClickOutside: true,
    onLoginOpenModal: null,
    overlays: [],

    pushOverlay: (
      componentName: Components,
      title: ReactNode,
      props?: {},
      type: ModalType = ModalType.MEDIUM,
    ) => {
      set((state) => ({
        overlays: [
          ...state.overlays,
          { title, type, component: ComponentFactory.create(componentName, props), footer: null },
        ],
      }));
    },

    openModal: (
      componentName: Components,
      title: ReactNode,
      props?: {},
      type: ModalType = ModalType.MEDIUM,
      footer: ReactNode = null,
      closeWhenClickOutside: boolean = true,
    ) => {
      document.body.style.overflowY = "hidden";
      hideCrispChat();
      set({
        isOpen: true,
        component: ComponentFactory.create(componentName, props),
        title,
        type,
        footer,
        closeWhenClickOutside,
        overlays: [],
      });
    },

    openSignIn: (params: PageParams, onLoginOpenModal: ModalState["onLoginOpenModal"] = null) => {
      const title = TKey.LOG_IN;
      document.body.style.overflowY = "hidden";
      hideCrispChat();
      set({
        isOpen: true,
        component: ComponentFactory.create(Components.SignIn, { params }),
        title,
        type: ModalType.SMALL,
        closeWhenClickOutside: true,
        onLoginOpenModal,
      });
    },

    openSignUp: (
      params: PageParams,
      onLoginOpenModal: ModalState["onLoginOpenModal"] = null,
      email?: string,
      inviteToken?: string,
    ) => {
      const title = TKey.SIGN_UP;
      document.body.style.overflowY = "hidden";
      hideCrispChat();
      set({
        isOpen: true,
        component: ComponentFactory.create(Components.SignUp, { params, email, inviteToken }),
        title,
        type: ModalType.MEDIUM,
        closeWhenClickOutside: true,
        onLoginOpenModal,
      });
    },

    openDemo: () => {
      getState().openModal(
        Components.CalendlyBooking,
        TKey.HOME_CTA_BOOK_DEMO,
        { url: process.env.NEXT_PUBLIC_DEMO_URL ?? "" },
        ModalType.LARGE,
      );
    },

    openBuyCredits: (params: PageParams) => {
      getState().openModal(Components.BuyCredits, TKey.CREDITS_BUY, { params }, ModalType.MEDIUM);
    },

    openJobFailure: (params: PageParams, jobs: ProcessingJobData[]) => {
      const title = createElement(
        "span",
        { style: { color: "var(--color-red-2)" } },
        jobs.length > 1 ? TKey.JOB_FAILURE_TITLE_PLURAL : TKey.JOB_FAILURE_TITLE,
      );

      getState().openModal(Components.JobFailure, title, { params, jobs }, ModalType.SMALL);
    },

    openImportedProfileAnalysisChoice: (
      params: PageParams,
      avatarId: number,
      pools: ImportedProfileAnalysisPools,
      needUpdate: boolean,
      onLaunched?: () => void,
    ) => {
      getState().openModal(
        Components.ImportedProfileAnalysisChoice,
        TKey.IMPORTED_PROFILE_ANALYSIS_CHOICE_TITLE,
        { params, avatarId, pools, needUpdate, onLaunched },
        ModalType.MEDIUM,
      );
    },

    openImportedProfileAnalysis: (
      params: PageParams,
      avatarId: number,
      poolTotal: number,
      poolDone: number,
      needUpdate: boolean,
      onLaunched?: () => void,
    ) => {
      getState().openModal(
        Components.ImportedProfileAnalysisOrder,
        TKey.IMPORTED_PROFILE_ANALYSIS_TITLE,
        { params, avatarId, poolTotal, poolDone, needUpdate, onLaunched },
        ModalType.SMALL,
      );
    },

    openImportedProfileExtensionGuide: (params: PageParams) => {
      getState().openModal(
        Components.ImportedProfileExtensionGuide,
        TKey.IMPORTED_PROFILE_EXTENSION_GUIDE_TITLE,
        { params },
        ModalType.SMALL,
      );
    },

    openImportedProfilesList: (
      params: PageParams,
      avatarId: number,
      criteria: ProspectSearchCriteria | null,
      tab: ImportedProfileListTab = ImportedProfileListTab.IMPORTED,
      onProspectAdded?: () => void,
    ) => {
      const titleKey =
        tab === ImportedProfileListTab.REJECTED
          ? TKey.IMPORTED_PROFILES_TAB_REJECTED
          : tab === ImportedProfileListTab.POTENTIAL
            ? TKey.IMPORTED_PROFILES_TAB_POTENTIAL
            : tab === ImportedProfileListTab.ANALYZED
              ? TKey.IMPORTED_PROFILES_TAB_ANALYZED
              : TKey.IMPORTED_PROFILES_TAB_IMPORTED;
      getState().openModal(
        Components.ImportedProfilesTabs,
        titleKey,
        { params, avatarId, criteria, tab, onProspectAdded },
        ModalType.MEDIUM,
      );
    },

    openProspectsFound: (params: PageParams, avatarId: number) => {
      getState().openModal(
        Components.ProspectsFound,
        TKey.PROSPECT_STAT_FOUND,
        { params, avatarId },
        ModalType.MEDIUM,
      );
    },

    openProspectForm: (
      params: PageParams,
      avatarId: number,
      criteria: ProspectSearchCriteria | null,
      onAdded?: () => void,
    ) => {
      getState().openModal(
        Components.ProspectForm,
        TKey.PROSPECT_SEARCH_ADD_MANUAL_TITLE,
        {
          lang: params.lang,
          criteria,
          inModal: true,
          requireConfirm: false,
          onSubmit: (input: ProspectInput, update: boolean) =>
            addClientProspect(avatarId, input, update),
          onAdded: () => {
            getState().closeModal();
            onAdded?.();
          },
        },
        ModalType.LARGE,
      );
    },

    openConfirmDialog: (
      params: PageParams,
      title: TKey,
      text: TKey,
      confirmLabel: TKey,
      onConfirm: () => void,
      overlay: boolean = false,
    ) => {
      if (overlay) {
        getState().pushOverlay(
          Components.ConfirmDialog,
          title,
          { params, text, confirmLabel, onConfirm, isOverlay: true },
          ModalType.SMALL,
        );
        return;
      }
      getState().openModal(
        Components.ConfirmDialog,
        title,
        { params, text, confirmLabel, onConfirm },
        ModalType.SMALL,
      );
    },

    openInsightCalls: (
      params: PageParams,
      insightId: number,
      source: InsightCallSource,
      title: string,
      filter: CallScopeFilter,
    ) => {
      if (getState().isOpen) {
        getState().pushOverlay(
          Components.InsightCalls,
          title,
          { params, insightId, source, filter, overlay: true },
          ModalType.MEDIUM,
        );
        return;
      }

      getState().openModal(
        Components.InsightCalls,
        title,
        { params, insightId, source, filter },
        ModalType.MEDIUM,
      );
    },

    openCallDetailOverlay: (params: PageParams, callFile: CallFile) => {
      getState().pushOverlay(
        Components.CallDetail,
        callFile.label ?? "",
        { params, callFile },
        ModalType.LARGE,
      );
    },

    openExportCall: (params: PageParams, callId: number) => {
      if (getState().isOpen) {
        getState().pushOverlay(
          Components.ExportCall,
          TKey.CALL_EXPORT_TITLE,
          { params, callId, isOverlay: true },
          ModalType.MEDIUM,
        );
        return;
      }

      getState().openModal(
        Components.ExportCall,
        TKey.CALL_EXPORT_TITLE,
        { params, callId },
        ModalType.MEDIUM,
      );
    },

    openAdminUserProfile: (params: PageParams, uuid: string, fullName?: string) => {
      getState().openModal(
        Components.AdminUserProfile,
        fullName || TKey.ADMIN_USERS,
        { params, uuid },
        ModalType.MEDIUM,
      );
    },

    openProspectDetail: (
      params: PageParams,
      prospectId: number,
      fullName: string,
      client: boolean = false,
      withContext?: boolean,
      withHistory?: boolean,
    ) => {
      if (getState().isOpen) {
        getState().pushOverlay(
          Components.ProspectDetail,
          fullName,
          { params, prospectId, client, overlay: true, withContext, withHistory },
          ModalType.MEDIUM,
        );
        return;
      }

      getState().openModal(
        Components.ProspectDetail,
        fullName,
        { params, prospectId, client, withContext, withHistory },
        ModalType.MEDIUM,
      );
    },

    openProspectActionConfirm: (
      params: PageParams,
      prospectId: number,
      kind: ProspectActionKind,
      label: string,
      typeId?: number,
      onDone?: () => void,
    ) => {
      const isOverlay = getState().isOpen;
      const props = { params, prospectId, kind, typeId, onDone, isOverlay };

      if (isOverlay) {
        getState().pushOverlay(Components.ProspectActionConfirm, label, props, ModalType.SMALL);
        return;
      }

      getState().openModal(Components.ProspectActionConfirm, label, props, ModalType.SMALL);
    },

    openProspectActionEdit: (
      params: PageParams,
      action: ProspectActionItem,
      label: string,
      onDone?: () => void,
    ) => {
      const isOverlay = getState().isOpen;
      const props = {
        params,
        prospectId: 0,
        kind: action.kind,
        actionId: action.id,
        doneAt: toInputDate(new Date(action.doneAt)),
        note: action.note ?? "",
        onDone,
        isOverlay,
      };

      if (isOverlay) {
        getState().pushOverlay(Components.ProspectActionConfirm, label, props, ModalType.SMALL);
        return;
      }

      getState().openModal(Components.ProspectActionConfirm, label, props, ModalType.SMALL);
    },

    openProspectActionType: (
      params: PageParams,
      projectId: number,
      onCreated?: (type: ProspectActionTypeItem) => void,
    ) => {
      getState().openModal(
        Components.ProspectActionType,
        TKey.PROSPECT_ACTION_CREATE_TYPE_TITLE,
        { params, projectId, onCreated },
        ModalType.SMALL,
      );
    },

    openProspectStatusReason: (
      params: PageParams,
      prospectId: number,
      status: ProspectStatus,
      onDone?: () => void,
    ) => {
      getState().openModal(
        Components.ProspectReject,
        status === ProspectStatus.QUALIFIED
          ? TKey.PROSPECT_SEARCH_QUALIFY_TITLE
          : TKey.PROSPECT_SEARCH_REJECT_TITLE,
        { params, prospectId, status, onDone },
        ModalType.SMALL,
      );
    },

    openProjectMemberForm: (
      params: PageParams,
      projectId: number,
      options: {
        member?: ProjectMemberData;
        onSaved?: (member: ProjectMemberData) => void;
      } = {},
    ) => {
      const { member, onSaved } = options;
      getState().openModal(
        Components.AddProjectMember,
        member ? TKey.PROJECT_MEMBER_EDIT : TKey.PROJECT_MEMBER_ADD,
        { params, projectId, member, onSaved },
        ModalType.SMALL,
      );
    },

    openDeleteProjectMember: (
      params: PageParams,
      projectId: number,
      member: ProjectMemberData,
      onDeleted?: (member: ProjectMemberData) => void,
    ) => {
      getState().openModal(
        Components.DeleteProjectMember,
        TKey.PROJECT_MEMBER_DELETE,
        { params, projectId, member, onDeleted },
        ModalType.SMALL,
      );
    },

    openScriptInsightQuotes: (
      params: PageParams,
      avatarId: number,
      insightId: number,
      label: string,
    ) => {
      getState().openModal(
        Components.ScriptInsightQuotes,
        label,
        { params, avatarId, insightId },
        ModalType.SMALL,
      );
    },

    openAddCallType: (
      params: PageParams,
      projectId: number,
      onCreated?: (name: string) => void,
    ) => {
      getState().openModal(
        Components.AddCallType,
        TKey.CALL_TYPE_ADD,
        { params, projectId, onCreated },
        ModalType.SMALL,
      );
    },

    openEditCallType: (
      params: PageParams,
      projectId: number,
      name: string,
      onSaved?: (name: string) => void,
    ) => {
      getState().openModal(
        Components.EditCallType,
        TKey.CALL_TYPE_EDIT,
        { params, projectId, name, onSaved },
        ModalType.SMALL,
      );
    },

    openDeleteCallType: (
      params: PageParams,
      projectId: number,
      name: string,
      onDeleted?: (name: string) => void,
    ) => {
      getState().openModal(
        Components.DeleteCallType,
        TKey.CALL_TYPE_DELETE,
        { params, projectId, name, onDeleted },
        ModalType.SMALL,
      );
    },

    openCostConfirmOverlay: (
      params: PageParams,
      title: string,
      props: Omit<CostConfirmProps, "params" | "isOverlay">,
    ) => {
      getState().pushOverlay(
        Components.CostConfirm,
        title,
        { ...props, params, isOverlay: true },
        ModalType.SMALL,
      );
    },

    openProspectFormOverlay: (
      params: PageParams,
      avatarId: number,
      criteria: ProspectSearchCriteria | null,
      initial: ProspectPrefill,
      onAdded?: (score: number) => void,
    ) => {
      getState().pushOverlay(
        Components.ProspectForm,
        TKey.PROSPECT_SEARCH_ADD_MANUAL_TITLE,
        {
          lang: params.lang,
          criteria,
          inModal: true,
          isOverlay: true,
          requireConfirm: false,
          initial,
          // Qualifying a detected potential: the created prospect is QUALIFIED.
          onSubmit: (input: ProspectInput, update: boolean) =>
            addClientProspect(avatarId, input, update, ProspectStatus.QUALIFIED),
          onAdded: (score: number) => {
            getState().closeOverlay();
            onAdded?.(score);
          },
        },
        ModalType.LARGE,
      );
    },

    openImportedProfileRejectOverlay: (
      params: PageParams,
      matchId: number,
      onRejected?: () => void,
      agent: boolean = false,
    ) => {
      getState().pushOverlay(
        Components.ImportedProfileRejectReason,
        TKey.PROSPECT_SEARCH_REJECT,
        { params, matchId, agent, onRejected },
        ModalType.SMALL,
      );
    },

    openCompanyMemberForm: (
      params: PageParams,
      companyId: number,
      onAdded?: () => void,
      member?: CompanyMemberItem,
    ) => {
      getState().pushOverlay(
        Components.CompanyMemberForm,
        member ? TKey.COMPANY_MEMBER_EDIT_TITLE : TKey.COMPANY_MEMBER_ADD_TITLE,
        { params, companyId, onAdded, member },
        ModalType.SMALL,
      );
    },

    closeOverlay: () => {
      set((state) => ({ overlays: state.overlays.slice(0, -1) }));
    },

    setFooter: (footer: ReactNode) => {
      set({ footer });
    },

    setOverlayFooter: (footer: ReactNode) => {
      set((state) => {
        if (state.overlays.length === 0) {
          return state;
        }
        const overlays = [...state.overlays];
        overlays[overlays.length - 1] = { ...overlays[overlays.length - 1], footer };
        return { overlays };
      });
    },

    closeModal: () => {
      document.body.style.overflowY = "";
      showCrispChat();
      set({
        isOpen: false,
        component: undefined,
        footer: null,
        title: "",
        overlays: [],
      });
    },
  })),
);
